import { Suspense, forwardRef, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { FormikProps } from "formik"
import { DateTime } from "luxon"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../core/api/errorTypes"
import { FetchSurveyDetailsResponse, fetchSurveyDetails, updateSurvey, SurveyQuery } from "../surveysListApi"
import { CreateOrEditSurveyForm } from "../components/CreateUpdateSurveyForm"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { MERCHANT_ADMIN_ROUTE, MERCHANT_ADMIN_SURVEYS } from "../../../../../constants/routes"
import { CreateOrEditSurveyFormValues } from "../merchantSurveyTypes"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"

const selectSurvey = (data: FetchSurveyDetailsResponse) => {
    const survey = data.survey
    if (!survey) return null

    return {
        ...survey,
        activePeriod: {
            from: {
                date: DateTime.fromISO(survey.activePeriod?.from?.date),
            },
            to: {
                date: survey.activePeriod?.to?.date ? DateTime.fromISO(survey.activePeriod?.to?.date) : null,
            },
        },
        card: {
            visibility: {
                ...survey.card.visibility,
                timeOfDay: survey.card.visibility.timeOfDay ? DateTime.fromISO(survey.card.visibility.timeOfDay) : null,
            },
        },
    }
}

const Survey = forwardRef<FormikProps<CreateOrEditSurveyFormValues>>((_, ref) => {
    const { surveyId } = useParams()
    const navigate = useNavigate()

    const surveyResult = useApiQuery(fetchSurveyDetails, {
        queryName: SurveyQuery.DETAILS,
        dependencies: {
            surveyId: surveyId ?? "",
        },
        enabled: Boolean(surveyId),
        select: selectSurvey,
        suspense: true,
        useErrorBoundary: true,
    })

    const { mutateAsync: callUpdateSurvey } = useApiMutation(updateSurvey, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [SurveyQuery.DETAILS] })
            await context.queryClient.invalidateQueries({ queryKey: [SurveyQuery.LIST] })
            navigate(MERCHANT_ADMIN_SURVEYS)
        },
    })

    return (
        <CreateOrEditSurveyForm
            ref={ref}
            initialValues={surveyResult.data}
            onSubmit={(data) => {
                surveyId && callUpdateSurvey({ survey: data, surveyId: surveyId })
            }}
        />
    )
})

export function EditSurvey() {
    const surveyFormRef = useRef<FormikProps<CreateOrEditSurveyFormValues>>(null)

    const handleSaveClick = () => {
        surveyFormRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="edit-survey-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SURVEYS_LIST_EDIT}
                    title="Surveys"
                    rightElement={
                        <LocalizedStrict id="edit-merchant-survey-save-button">
                            <StandardButton type="submit" onClick={handleSaveClick}>
                                Edit Survey
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-surveys">
                            <BreadcrumbLink to={MERCHANT_ADMIN_SURVEYS}>Surveys</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="nav-item-edit-survey-header">
                            <Breadcrumb>Edit Survey</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Survey ref={surveyFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
